import { useState } from 'react';
import {
  Button,
  Group,
  NumberInput,
  Paper,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconClock,
  IconFileInvoice,
  IconSend,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import { useCustomerProfile, useSubmitNetTermsRequest } from '@/hooks/usePortalApi';
import type { NetTermsChangeRequest } from '@/types';

const TERMS_OPTIONS = [
  { value: 'COD', label: 'COD (Cash on Delivery)' },
  { value: 'Net 7', label: 'Net 7' },
  { value: 'Net 10', label: 'Net 10' },
  { value: 'Net 15', label: 'Net 15' },
  { value: 'Net 21', label: 'Net 21' },
  { value: 'Net 30', label: 'Net 30' },
  { value: 'Net 45', label: 'Net 45' },
  { value: 'Net 60', label: 'Net 60' },
  { value: 'Net 90', label: 'Net 90' },
];

function formatCurrency(val: string | number) {
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return '--';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
}

export default function NetTermsRequest() {
  const { data: profile, isLoading: profileLoading } = useCustomerProfile();
  const submitRequest = useSubmitNetTermsRequest();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<Omit<NetTermsChangeRequest, 'current_terms'>>({
    initialValues: {
      requested_terms: '',
      reason: '',
      annual_volume: '',
      years_as_customer: undefined,
    },
    validate: {
      requested_terms: (v) => (!v ? 'Requested terms is required' : null),
      reason: (v) => (!v || v.trim().length === 0 ? 'Reason is required' : null),
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    const payload: NetTermsChangeRequest = {
      ...values,
      current_terms: profile?.payment_terms,
      annual_volume: values.annual_volume || undefined,
      years_as_customer: values.years_as_customer ?? undefined,
    };

    submitRequest.mutate(payload, {
      onSuccess: () => {
        notifications.show({
          title: 'Request Submitted',
          message:
            'Your net terms change request has been submitted. Our accounting team will review it within 2-3 business days.',
          color: 'green',
        });
        setSubmitted(true);
      },
      onError: () => {
        notifications.show({
          title: 'Error',
          message: 'Failed to submit net terms request. Please try again.',
          color: 'red',
        });
      },
    });
  };

  if (submitted) {
    return (
      <Stack gap="lg">
        <PageHeader
          title="Request Net Terms Change"
          description="Submit a request to change your payment terms"
        />
        <Paper p="xl" shadow="sm">
          <Stack align="center" gap="md" py="xl">
            <IconCheck size={48} color="var(--mantine-color-green-6)" />
            <Title order={3}>Request Submitted</Title>
            <Text c="dimmed" ta="center" maw={500}>
              Your net terms change request has been submitted. Our accounting
              team will review it within 2-3 business days.
            </Text>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Request Net Terms Change"
        description="Submit a request to change your payment terms"
      />

      {/* Section 1: Current Terms */}
      {profileLoading ? (
        <Skeleton height={120} radius="md" />
      ) : (
        <Paper p="lg" shadow="sm" bg="gray.0">
          <Stack gap="md">
            <Group gap="xs">
              <IconClock size={20} />
              <Text fw={700} size="lg">
                Current Terms
              </Text>
            </Group>

            <Group gap="xl">
              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  Payment Terms
                </Text>
                <Text fw={600}>
                  {profile?.payment_terms || '--'}
                </Text>
              </Stack>

              {profile?.credit_limit && (
                <Stack gap={2}>
                  <Text size="sm" c="dimmed">
                    Credit Limit
                  </Text>
                  <Text fw={600}>{formatCurrency(profile.credit_limit)}</Text>
                </Stack>
              )}

              {profile?.balance && (
                <Stack gap={2}>
                  <Text size="sm" c="dimmed">
                    Current Balance
                  </Text>
                  <Text fw={600}>{formatCurrency(profile.balance)}</Text>
                </Stack>
              )}
            </Group>
          </Stack>
        </Paper>
      )}

      {/* Section 2: Request Form */}
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Paper p="lg" shadow="sm">
          <Stack gap="md">
            <Group gap="xs">
              <IconFileInvoice size={20} />
              <Text fw={700} size="lg">
                Request Details
              </Text>
            </Group>

            <Select
              label="Requested Terms"
              placeholder="Select desired payment terms"
              data={TERMS_OPTIONS}
              required
              {...form.getInputProps('requested_terms')}
            />

            <Textarea
              label="Reason for Change"
              placeholder="Please explain why you're requesting this change..."
              minRows={4}
              required
              {...form.getInputProps('reason')}
            />

            <TextInput
              label="Estimated Annual Volume"
              placeholder="e.g., $250,000"
              {...form.getInputProps('annual_volume')}
            />

            <NumberInput
              label="Years as Customer"
              placeholder="e.g., 5"
              min={0}
              {...form.getInputProps('years_as_customer')}
            />

            <Group justify="flex-end" mt="md">
              <Button
                type="submit"
                leftSection={<IconSend size={16} />}
                loading={submitRequest.isPending}
              >
                Submit Request
              </Button>
            </Group>
          </Stack>
        </Paper>
      </form>
    </Stack>
  );
}
